import { toast } from '@foam/buoy/src/components/Toast/Toast';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ContentsApiResponseResultsType } from '~/searchApi';

import { usePageBuilderStore } from '../../hooks/usePageBuilderStore';
import { useAddContentStore } from '../../sidebar/useAddContentStore';
import {
  MKTalentContentFields,
  TalentContentProperties,
} from './TalentContent';

// Helper function to extract only the needed fields
const extractContentFields = (
  content: ContentsApiResponseResultsType,
): MKTalentContentFields => ({
  assets: content.assets,
  creator: content.creator,
  id: content.id,
  platform: content.platform,
  share_link: content.share_link,
  external_url: content.external_url,
  network_id: content.network_id,
  content_type: content.content_type,
  status: content.status,
});

export const MAX_SELECTION_LIMIT = 64;

export const useContentSelection = () => {
  const {
    isEditMode,
    blockId,
    callback,
    reset,
    selectedContent,
    setSelectedContent,
    modifiedContents,
    setModifiedContents,
    showInShared,
    setShowInShared,
  } = useAddContentStore();
  const { blocks } = usePageBuilderStore();

  const talentContentBlock = isEditMode
    ? blocks.find((b) => b.component === 'TalentContent' && b.id === blockId)
    : undefined;

  // Set showInShared from block properties if it's true
  useEffect(() => {
    if (talentContentBlock?.properties) {
      const showInSharedValue = (
        talentContentBlock.properties as TalentContentProperties
      ).showInShared;
      setShowInShared(showInSharedValue);
    } else {
      // Reset to false when not in edit mode or when block properties are undefined
      setShowInShared(false);
    }
  }, [talentContentBlock, setShowInShared]);

  // Get the existing contents from the block (if any)
  const existingContents = useMemo(() => {
    if (!talentContentBlock) return [];
    return [
      ...(talentContentBlock.properties as TalentContentProperties)
        .customContent,
    ];
  }, [talentContentBlock]);

  // If the user has modified the selection, use that; otherwise fall back to existing contents.
  const baseContents = useMemo(
    () => (modifiedContents !== null ? modifiedContents : existingContents),
    [modifiedContents, existingContents],
  );

  // Use refs to access latest values without causing callback reference changes
  const stateRef = useRef({
    modifiedContents,
    existingContents,
    selectedContent,
  });
  stateRef.current = { modifiedContents, existingContents, selectedContent };

  // Stable callback that doesn't change reference between renders
  const handleSelectContent = useCallback(
    (checked: boolean, content: ContentsApiResponseResultsType) => {
      const { modifiedContents, existingContents, selectedContent } =
        stateRef.current;

      // If no modification has been made yet, initialize modifiedContents with existing contents
      if (modifiedContents === null) {
        setModifiedContents(existingContents);
      }

      // Get the current contents, ensuring it's always an array
      const currentContents =
        modifiedContents !== null ? modifiedContents : existingContents;

      if (checked) {
        // Check if the limit of MAX_SELECTION_LIMIT items has been reached
        if (currentContents.length >= MAX_SELECTION_LIMIT) {
          return;
        }

        // Add the content if it isn't already there.
        if (!currentContents.some((c) => c.id === content.id)) {
          const updatedContents = [...currentContents, content];
          setModifiedContents(updatedContents);
          setSelectedContent([...selectedContent, content]);

          // Show toast when user selects the MAX_SELECTION_LIMIT item
          if (updatedContents.length === MAX_SELECTION_LIMIT) {
            toast(
              `You've reached the maximum selection limit of ${MAX_SELECTION_LIMIT} items`,
              {
                variant: 'alert',
                duration: 4000,
              },
            );
          }
        }
      } else {
        // Remove the content from the list.
        const updatedContents = currentContents.filter(
          (c) => c.id !== content.id,
        );
        setModifiedContents(updatedContents);
        setSelectedContent(selectedContent.filter((c) => c.id !== content.id));
      }
    },
    [setModifiedContents, setSelectedContent],
  );

  const handleClose = useCallback(() => {
    setSelectedContent([]);
    // In edit mode, clear all content (including existing); in add mode, reset to initial state
    setModifiedContents(isEditMode ? [] : null);
  }, [setSelectedContent, setModifiedContents, isEditMode]);

  const handleAddEditContent = useCallback(() => {
    const finalContents =
      modifiedContents !== null ? modifiedContents : existingContents;

    if (typeof callback === 'function') {
      // Extract only the needed fields when calling the callback
      const selectedFieldsContents = finalContents.map(extractContentFields);
      callback(
        selectedFieldsContents as TalentContentProperties['customContent'],
        showInShared,
      );
    }
    reset();
  }, [modifiedContents, existingContents, callback, reset, showInShared]);

  // Stable callback that uses ref to access latest state
  const getIsChecked = useCallback((contentId: string) => {
    const { modifiedContents, existingContents } = stateRef.current;
    const currentContents =
      modifiedContents !== null ? modifiedContents : existingContents;
    return currentContents.some((content) => content.id === contentId);
  }, []);

  // Check if there are actual changes compared to existing contents
  const hasChanges = useMemo(() => {
    const savedShowInShared =
      (talentContentBlock?.properties as TalentContentProperties | undefined)
        ?.showInShared ?? false;
    const showInSharedChanged = showInShared !== savedShowInShared;

    if (modifiedContents === null) return showInSharedChanged;
    if (modifiedContents.length !== existingContents.length) return true;
    // Compare IDs to check if contents are different
    const existingIds = new Set(existingContents.map((c) => c.id));
    const contentChanged = modifiedContents.some((c) => !existingIds.has(c.id));

    return contentChanged || showInSharedChanged;
  }, [modifiedContents, existingContents, showInShared, talentContentBlock]);

  return {
    selectedContent,
    modifiedContents:
      modifiedContents !== null ? modifiedContents : existingContents,
    handleSelectContent,
    handleClose,
    handleAddEditContent,
    getIsChecked,
    baseContents,
    hasChanges,
  };
};
